import { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Group, Stack, Text, SimpleGrid, Loader, Box, Alert } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { IconCalendar, IconArrowLeft, IconCheck } from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { showNotification } from '@mantine/notifications'
import type { Slot } from '@medplum/fhirtypes'
import { useMiddlewareClient } from '@/hooks'
import { useClient } from '@/hooks/useClient'
import { ZOCDOC_APPOINTMENT_SYSTEM } from '@/model/middleware-types'
import { buildTimeOptions, combineDateAndTime } from '../../utils'
import { createSingleSlot } from '../../service'

const VR_CLASS = 'http://terminology.hl7.org/CodeSystem/v3-ActCode|VR'

const SLOT_DURATION_MIN = 30
const TIME_OPTIONS = buildTimeOptions(8, 20, SLOT_DURATION_MIN).filter((o) => o.value <= '19:30')

type SlotStatus = 'free' | 'busy' | 'none' | 'patient'
type Step = 'date' | 'time' | 'confirm' | 'closeWarning'

export interface RescheduleAppointmentModalProps {
  opened: boolean
  onClose: () => void
  onSuccess: () => void
  encounterId: string
  practitionerId?: string
  patientId?: string
  orgPractitionerIds?: string[]
  currentStart?: string
  currentEnd?: string
  patientName?: string
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function localHHMM(iso: string): string {
  const d = new Date(iso)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const SLOT_COLORS: Record<SlotStatus, { bg: string; border: string; legend: string }> = {
  free: { bg: '#e6f9ee', border: '#51cf66', legend: '#51cf66' },
  busy: { bg: '#fff7e0', border: '#f0b429', legend: '#f0b429' },
  none: { bg: '#f1f3f5', border: '#dee2e6', legend: '#adb5bd' },
  patient: { bg: '#fff0f0', border: '#f03e3e', legend: '#f03e3e' },
}

export function RescheduleAppointmentModal(props: RescheduleAppointmentModalProps) {
  const {
    opened,
    onClose,
    onSuccess,
    encounterId,
    practitionerId,
    patientId,
    orgPractitionerIds,
    currentStart,
    currentEnd,
    patientName,
  } = props
  const medplum = useMedplum()
  const middleware = useMiddlewareClient()
  const client = useClient()

  const [step, setStep] = useState<Step>('date')
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTime, setSelectedTime] = useState<string | null>(null)
  const [slotStatusByTime, setSlotStatusByTime] = useState<Record<string, SlotStatus>>({})
  const [patientBusyTimes, setPatientBusyTimes] = useState<Set<string>>(new Set())
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [alreadyCancelled, setAlreadyCancelled] = useState(false)
  const [rebookError, setRebookError] = useState<string | null>(null)
  // Persists the patient ID resolved from the encounter so retries after a failed booking
  // can still book without re-reading a potentially-cancelled encounter.
  const [resolvedPatientId, setResolvedPatientId] = useState<string | null>(null)
  /**
   * The Zocdoc booking this visit came from, read BEFORE the cancel and held across the two-phase
   * flow. Rescheduling here is a cancel plus a rebook, and the cancel hard-deletes the Appointment
   * that carries the link — so unless it is carried onto the new one, Zocdoc keeps sending events
   * for a booking nothing on our side answers to.
   */
  const [zocdocAppointmentId, setZocdocAppointmentId] = useState<string | null>(null)

  useEffect(() => {
    if (opened) {
      setStep('date')
      setSelectedDate(null)
      setSelectedTime(null)
      setSlotStatusByTime({})
      setPatientBusyTimes(new Set())
      setLoadingSlots(false)
      setSubmitting(false)
      setAlreadyCancelled(false)
      setRebookError(null)
      setResolvedPatientId(null)
    }
  }, [opened])

  useEffect(() => {
    if (step !== 'time' || !selectedDate || !practitionerId) {
      return
    }
    let cancelled = false
    setLoadingSlots(true)
    void (async () => {
      try {
        const dayStart = new Date(selectedDate)
        dayStart.setHours(0, 0, 0, 0)
        const dayEnd = new Date(dayStart.getTime() + 24 * 3600 * 1000)

        const [schedule, patientEncounters] = await Promise.all([
          medplum.searchOne('Schedule', `actor=Practitioner/${practitionerId}`),
          patientId
            ? medplum.searchResources(
                'Encounter',
                `subject=Patient/${patientId}&date=ge${dayStart.toISOString()}&date=lt${dayEnd.toISOString()}&status:not=cancelled&class=${VR_CLASS}&_count=200`,
              )
            : Promise.resolve([]),
        ])

        if (cancelled) return

        const map: Record<string, SlotStatus> = {}
        if (schedule?.id) {
          const slots = (await medplum.searchResources(
            'Slot',
            `schedule=Schedule/${schedule.id}&start=ge${dayStart.toISOString()}&start=lt${dayEnd.toISOString()}&_count=500`,
          )) as Slot[]
          if (!cancelled) {
            for (const slot of slots) {
              if (!slot.start) continue
              map[localHHMM(slot.start)] = slot.status === 'free' ? 'free' : 'busy'
            }
          }
        }

        const orgSet = new Set(orgPractitionerIds ?? [])
        const busySet = new Set<string>()
        for (const enc of patientEncounters) {
          if (enc.id === encounterId) continue
          if (!enc.period?.start || !enc.period?.end) continue
          const encPractitionerId = enc.participant
            ?.find((p) => p.individual?.reference?.startsWith('Practitioner/'))
            ?.individual?.reference?.split('/')[1]
          if (orgSet.size > 0 && (!encPractitionerId || !orgSet.has(encPractitionerId))) continue
          const encStart = new Date(enc.period.start).getTime()
          const encEnd = new Date(enc.period.end).getTime()
          for (const opt of TIME_OPTIONS) {
            const d = new Date(selectedDate)
            const [h, m] = opt.value.split(':').map(Number)
            d.setHours(h, m, 0, 0)
            const slotStart = d.getTime()
            const slotEnd = slotStart + SLOT_DURATION_MIN * 60000
            if (slotStart < encEnd && slotEnd > encStart) {
              busySet.add(opt.value)
            }
          }
        }

        if (!cancelled) {
          setSlotStatusByTime(map)
          setPatientBusyTimes(busySet)
        }
      } catch {
        if (!cancelled) {
          setSlotStatusByTime({})
          setPatientBusyTimes(new Set())
        }
      } finally {
        if (!cancelled) setLoadingSlots(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [step, selectedDate, practitionerId, patientId, orgPractitionerIds, encounterId, medplum])

  const newStartIso = useMemo(() => {
    if (!selectedDate || !selectedTime) return null
    return combineDateAndTime(new Date(selectedDate), selectedTime)
  }, [selectedDate, selectedTime])

  const newEndIso = useMemo(() => {
    if (!newStartIso) return null
    return new Date(new Date(newStartIso).getTime() + SLOT_DURATION_MIN * 60000).toISOString()
  }, [newStartIso])

  const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—')
  const fmtTime = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—'

  /**
   * The visit's Zocdoc booking id, read off the Appointment while it still exists.
   *
   * **"Could not read" is not "not bound".** A swallowed failure used to answer `undefined`, which
   * sent a Zocdoc-bound visit down the unbound path: the beHuman visit was cancelled and rebooked
   * at the new time while the Zocdoc booking stayed at the old one — the exact divergence the
   * bound path exists to prevent — and a second failed read lost the link with it. So the two
   * answers are kept apart and the caller refuses rather than guesses.
   */
  const readZocdocBinding = async (): Promise<
    { state: 'bound'; id: string } | { state: 'unbound' } | { state: 'unreadable' }
  > => {
    try {
      const encounter = await medplum.readResource('Encounter', encounterId)
      const appointmentId = encounter.appointment?.[0]?.reference?.split('/')[1]
      if (!appointmentId) return { state: 'unbound' }
      const appointment = await medplum.readResource('Appointment', appointmentId)
      const id = appointment.identifier?.find(
        (candidate) => candidate.system === ZOCDOC_APPOINTMENT_SYSTEM,
      )?.value
      return id ? { state: 'bound', id } : { state: 'unbound' }
    } catch {
      return { state: 'unreadable' }
    }
  }

  const handleConfirm = async () => {
    if (!selectedDate || !newStartIso || !newEndIso || !practitionerId) return
    try {
      setSubmitting(true)

      const schedule = await medplum.searchOne('Schedule', `actor=Practitioner/${practitionerId}`)
      if (!schedule?.id) {
        showNotification({ color: 'red', message: 'This provider has no schedule' })
        return
      }

      let currentPatientId = patientId ?? resolvedPatientId
      if (!currentPatientId && !alreadyCancelled) {
        const encounter = await medplum.readResource('Encounter', encounterId)
        currentPatientId = encounter.subject?.reference?.split('/')[1] ?? null
        if (currentPatientId) setResolvedPatientId(currentPatientId)
      }
      if (!currentPatientId) {
        showNotification({ color: 'red', message: 'Could not resolve the patient' })
        return
      }

      const dayStart = new Date(selectedDate)
      dayStart.setHours(0, 0, 0, 0)
      const dayEnd = new Date(dayStart.getTime() + 24 * 3600 * 1000)
      const newStartMs = new Date(newStartIso).getTime()
      const newEndMs = new Date(newEndIso).getTime()
      const dayEncounters = await medplum.searchResources(
        'Encounter',
        `practitioner=Practitioner/${practitionerId}&date=ge${dayStart.toISOString()}&date=lt${dayEnd.toISOString()}&status:not=cancelled&class=${VR_CLASS}&_count=200`,
      )
      const hasConflict = dayEncounters.some((e) => {
        if (e.id === encounterId) return false
        const s = e.period?.start ? new Date(e.period.start).getTime() : NaN
        const en = e.period?.end ? new Date(e.period.end).getTime() : NaN
        if (Number.isNaN(s) || Number.isNaN(en)) return false
        return s < newEndMs && en > newStartMs
      })
      if (hasConflict) {
        showNotification({
          color: 'red',
          message: 'The provider already has an appointment at this time. Pick another time.',
        })
        return
      }

      // **A Zocdoc-bound visit is MOVED, never cancelled and rebooked.** Deleting the visit and
      // booking a new one only ever changed beHuman: Zocdoc kept the original time, and the two
      // calendars came apart while still linked. The middleware's reschedule does the whole job —
      // `POST /reschedule`, the beHuman move in place, then `POST /confirm` — exactly as the
      // Zocdoc console's own reschedule does, because it is the same handler.
      //
      // Read before anything is written, and only when nothing has been cancelled yet: on a retry
      // after a partial failure the old Appointment is already gone.
      if (!alreadyCancelled) {
        const binding = await readZocdocBinding()

        // Nothing is written when we could not tell. Guessing "unbound" here is what split the two
        // calendars; a retry costs the operator seconds, and the visit is still exactly as it was.
        if (binding.state === 'unreadable') {
          showNotification({
            color: 'red',
            title: 'Not rescheduled',
            message:
              'Could not read the appointment to see whether it is booked through Zocdoc. Nothing was changed — please try again.',
          })
          return
        }

        if (binding.state === 'bound') {
          const moved = await client.zocdocRescheduleAppointment({
            zocdocAppointmentId: binding.id,
            // The instant, not a wall clock: this picker works off the beHuman calendar and has no
            // business guessing which local time that is in Zocdoc's location zone.
            startAt: newStartIso,
            // The practitioner picked a time on their own calendar, so committing them to it is
            // what they just asked for.
            createSlot: true,
          })

          if (moved.status === 'refused') {
            showNotification({
              color: 'red',
              title: 'Not rescheduled',
              message: moved.refusal?.detail ?? 'Zocdoc refused the change',
            })
            return
          }

          // Both calendars hold the new time by here. The Zocdoc status is the one thing that can
          // still be short of where we wanted it, and it is said out loud rather than folded away.
          showNotification({
            color: moved.behumanMoved ? 'green' : 'orange',
            message: moved.behumanMoved
              ? 'Appointment rescheduled, on Zocdoc too'
              : `Zocdoc moved to the new time, but the beHuman visit did not — ${moved.behumanFailure ?? 'the calendar write failed'}. Fix it on the calendar.`,
          })
          onSuccess()
          onClose()
          return
        }
      }

      if (!alreadyCancelled) {
        // Read the link while the Appointment still exists. A failure here must not block the
        // reschedule: losing the Zocdoc binding is worse than not rescheduling, but not by enough
        // to strand the patient's visit.
        // Unbound by the check above, so this only carries a link that appeared in between —
        // and an unreadable second attempt cannot strand the visit, because there is none to lose.
        const carried = await readZocdocBinding()
        if (carried.state === 'bound') setZocdocAppointmentId(carried.id)

        const cancelRes = (await middleware.delete('/appointment/{encounterId}', {
          encounterId,
        })) as { success?: boolean; message?: string }
        if (!cancelRes?.success) {
          showNotification({
            color: 'red',
            message: cancelRes?.message || 'Could not cancel the original appointment',
          })
          return
        }
        setAlreadyCancelled(true)
      }

      await createSingleSlot({
        medplum,
        schedule,
        start: newStartIso,
        end: newEndIso,
        status: 'free',
      })

      const freeSlots = await medplum.searchResources(
        'Slot',
        `schedule=Schedule/${schedule.id}&status=free&start=${newStartIso}&_count=1`,
      )
      const slot = freeSlots[0]
      if (!slot?.id) {
        showNotification({
          color: 'red',
          message: 'Could not create the new time slot. Please pick another time.',
        })
        return
      }

      const bookRes = (await middleware.post('/appointment/book', {
        patientId: currentPatientId,
        slot: slot as unknown as Record<string, unknown>,
        ...(zocdocAppointmentId && { zocdocAppointmentId }),
      })) as { success?: boolean; message?: string }
      if (!bookRes?.success) {
        showNotification({
          color: 'red',
          message: bookRes?.message || 'Could not book the appointment. Please pick another time.',
        })
        return
      }

      showNotification({ color: 'green', message: 'Appointment rescheduled successfully' })
      onSuccess()
      onClose()
    } catch (error) {
      showNotification({
        color: 'red',
        title: 'Reschedule failed',
        message: (error as Error)?.message || 'Could not reschedule appointment',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (alreadyCancelled) {
      setStep('closeWarning')
      return
    }
    onClose()
  }

  const rebookAtOriginalTime = async () => {
    if (!currentStart || !practitionerId || !patientId) {
      setRebookError('Cannot rebook: missing original time or provider')
      return
    }
    if (new Date(currentStart).getTime() <= Date.now()) {
      setRebookError(
        'The original appointment time has already passed. Please close and book a new appointment manually.',
      )
      return
    }
    const effectiveEnd =
      currentEnd ??
      new Date(new Date(currentStart).getTime() + SLOT_DURATION_MIN * 60000).toISOString()
    setRebookError(null)
    try {
      setSubmitting(true)
      const schedule = await medplum.searchOne('Schedule', `actor=Practitioner/${practitionerId}`)
      if (!schedule?.id) {
        setRebookError('This provider has no schedule. Please book manually.')
        return
      }
      await createSingleSlot({
        medplum,
        schedule,
        start: currentStart,
        end: effectiveEnd,
        status: 'free',
      })
      const freeSlots = await medplum.searchResources(
        'Slot',
        `schedule=Schedule/${schedule.id}&status=free&start=${currentStart}&_count=1`,
      )
      const slot = freeSlots[0]
      if (!slot?.id) {
        setRebookError('Could not create the original slot. Please close and book manually.')
        return
      }
      const bookRes = (await middleware.post('/appointment/book', {
        patientId,
        slot: slot as unknown as Record<string, unknown>,
        // The recovery path puts the visit back where it was, so it has to put the Zocdoc link
        // back too — otherwise undoing a failed reschedule quietly orphans the booking.
        ...(zocdocAppointmentId && { zocdocAppointmentId }),
      })) as { success?: boolean; message?: string }
      if (!bookRes?.success) {
        setRebookError(
          bookRes?.message ?? 'Could not rebook at original time. Please close and book manually.',
        )
        return
      }
      showNotification({ color: 'green', message: 'Appointment rebooked at original time' })
      onSuccess()
      onClose()
    } catch (error) {
      setRebookError(
        (error as Error)?.message ??
          'Could not rebook appointment. Please close and book manually.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Reschedule appointment"
      centered
      size="lg"
      withCloseButton={step !== 'closeWarning'}
    >
      {step === 'date' && (
        <Stack>
          <Text size="sm" c="dimmed">
            Pick a new date{patientName ? ` for ${patientName}` : ''}. On the next step you can
            choose any time — even outside available slots.
          </Text>
          <DatePickerInput
            leftSection={<IconCalendar size={16} />}
            label="New date"
            placeholder="Select date"
            value={selectedDate}
            onChange={(value) => {
              if (!value) {
                setSelectedDate(null)
                return
              }
              const raw = value as unknown as string | Date
              let d: Date
              if (typeof raw === 'string') {
                const parts = raw.split('-').map(Number)
                if (parts.length !== 3) {
                  setSelectedDate(null)
                  return
                }
                d = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0, 0)
              } else {
                d = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate(), 12, 0, 0, 0)
              }
              if (!Number.isNaN(d.getTime())) setSelectedDate(d)
              else setSelectedDate(null)
            }}
            minDate={new Date()}
            radius="md"
            size="md"
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button disabled={!selectedDate} onClick={() => setStep('time')}>
              Next
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'time' && (
        <Stack>
          <Group justify="space-between">
            <Text size="sm" fw={600}>
              {selectedDate?.toLocaleDateString()}
            </Text>
            <Group gap="md">
              <LegendDot status="free" label="Free" />
              <LegendDot status="busy" label="Busy" />
              <LegendDot status="none" label="No slot" />
              {patientId && <LegendDot status="patient" label="Patient busy" />}
            </Group>
          </Group>

          {loadingSlots ? (
            <Group justify="center" p="xl">
              <Loader />
            </Group>
          ) : (
            <SimpleGrid cols={4} spacing="xs">
              {TIME_OPTIONS.map((opt) => {
                const patientConflict = patientBusyTimes.has(opt.value)
                const status = patientConflict ? 'patient' : (slotStatusByTime[opt.value] ?? 'none')
                const colors = SLOT_COLORS[status]
                return (
                  <Box
                    key={opt.value}
                    component="button"
                    type="button"
                    disabled={patientConflict}
                    onClick={() => {
                      setSelectedTime(opt.value)
                      setStep('confirm')
                    }}
                    style={{
                      cursor: patientConflict ? 'not-allowed' : 'pointer',
                      padding: '6px 4px 4px',
                      borderRadius: 8,
                      background: colors.bg,
                      border: `1px solid ${colors.border}`,
                      fontSize: 13,
                      fontWeight: 500,
                      opacity: patientConflict ? 0.7 : 1,
                      transition: 'transform 0.05s ease',
                      lineHeight: 1.2,
                    }}
                  >
                    {opt.label}
                    {patientConflict && (
                      <Text size="9px" c="red" lh={1} mt={2}>
                        patient busy
                      </Text>
                    )}
                  </Box>
                )
              })}
            </SimpleGrid>
          )}

          <Group justify="flex-start">
            <Button
              variant="outline"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => setStep('date')}
            >
              Back
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'confirm' && (
        <Stack>
          <Text size="sm" c="dimmed">
            Review the change{patientName ? ` for ${patientName}` : ''} before confirming.
          </Text>

          <SimpleGrid cols={2} spacing="md">
            <SummaryCard
              label="Current"
              dateLabel={fmtDate(currentStart)}
              timeLabel={fmtTime(currentStart)}
            />
            <SummaryCard
              label="New"
              dateLabel={fmtDate(newStartIso)}
              timeLabel={fmtTime(newStartIso)}
              highlight
            />
          </SimpleGrid>

          <Group justify="space-between" mt="sm">
            <Button
              variant="outline"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => setStep('time')}
              disabled={submitting}
            >
              Back
            </Button>
            <Button
              leftSection={!submitting && <IconCheck size={16} />}
              onClick={handleConfirm}
              loading={submitting}
            >
              Confirm reschedule
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'closeWarning' && (
        <Stack>
          <Alert color="orange" title="Original appointment was cancelled">
            The original appointment was cancelled but a new one was not booked.
            {currentStart && (
              <Text size="sm" mt={4}>
                Original time: <strong>{fmtDate(currentStart)}</strong>{' '}
                <strong>{fmtTime(currentStart)}</strong>
              </Text>
            )}
          </Alert>
          {rebookError && (
            <Alert color="red" title="Rebook failed">
              {rebookError}
            </Alert>
          )}
          <Group justify="space-between" mt="sm">
            <Button
              variant="subtle"
              color="red"
              onClick={() => {
                showNotification({
                  color: 'orange',
                  title: 'Appointment cancelled',
                  message: 'The original appointment was cancelled and was not rebooked.',
                })
                onSuccess()
                onClose()
              }}
            >
              Close without rebooking
            </Button>
            <Button
              loading={submitting}
              disabled={!currentStart || new Date(currentStart).getTime() <= Date.now()}
              onClick={() => void rebookAtOriginalTime()}
            >
              Rebook at original time
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  )
}

function SummaryCard({
  label,
  dateLabel,
  timeLabel,
  highlight = false,
}: {
  label: string
  dateLabel: string
  timeLabel: string
  highlight?: boolean
}) {
  return (
    <Box
      style={{
        borderRadius: 8,
        padding: 12,
        background: highlight ? '#eef4fb' : '#f8f9fa',
        border: `1px solid ${highlight ? '#316dab' : '#dee2e6'}`,
      }}
    >
      <Group gap={6} mb={6}>
        <IconCalendar size={14} color={highlight ? '#316dab' : '#868e96'} />
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          {label}
        </Text>
      </Group>
      <Text fw={600} size="sm">
        {dateLabel}
      </Text>
      <Text size="sm" c="dimmed">
        {timeLabel}
      </Text>
    </Box>
  )
}

function LegendDot({ status, label }: { status: SlotStatus; label: string }) {
  return (
    <Group gap={4}>
      <span
        style={{
          width: 9,
          height: 9,
          borderRadius: 999,
          background: SLOT_COLORS[status].legend,
          display: 'inline-block',
        }}
      />
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  )
}
